import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Save, Eye, Upload, FileText, Video, Image, Link, Trash2, Edit, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { api, API_BASE_URL } from '../../api';
import { v4 as uuidv4 } from 'uuid'; // Import uuidv4

interface Category {
  _id: string;
  name: string;
}

interface Module {
  _id?: string;
  title: string;
  description: string;
  type: 'video' | 'pdf' | 'text' | 'image' | 'document' | 'link';
  content?: string;
  fileUrl?: string;
  externalUrl?: string;
  file?: File;
  order?: number;
  duration: number;
  isRequired: boolean;
  fileKey?: string;
}

interface Section {
  _id?: string;
  title: string;
  description: string;
  order?: number;
  modules: Module[];
}

interface CourseForm {
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  price: number;
  originalPrice?: number;
  level: 'beginner' | 'intermediate' | 'advanced';
  tags: string;
  requirements: string;
  objectives: string;
  targetAudience: string;
  settings: {
    allowDownload: boolean;
    allowComments: boolean;
    sequentialAccess: boolean;
    timeLimit: number;
  };
  certificate: {
    enabled: boolean;
    passingScore: number;
  };
  estimatedDuration: number;
  isPublished: boolean;
  status: 'draft' | 'pending' | 'published' | 'archived';
  sections: Section[];
}

const CourseEdit: React.FC = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'content' | 'settings'>('info');
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [editingModule, setEditingModule] = useState<{ sectionId: string; module: Module | null }>({ 
    sectionId: '', 
    module: null 
  });

  const { 
    register, 
    handleSubmit, 
    setValue, 
    watch, 
    formState: { errors } 
  } = useForm<CourseForm>({
    defaultValues: {
      title: '',
      description: '',
      shortDescription: '',
      category: '',
      price: 0,
      originalPrice: undefined,
      level: 'beginner',
      tags: '',
      requirements: '',
      objectives: '',
      targetAudience: '',
      settings: {
        allowDownload: false,
        allowComments: false,
        sequentialAccess: false,
        timeLimit: 0
      },
      certificate: {
        enabled: false,
        passingScore: 70
      },
      estimatedDuration: 0,
      isPublished: false,
      status: 'draft',
      sections: [] // Initialize sections as an empty array
    }
  });

  const sections = watch('sections');

  const levels = [
    { value: 'beginner', label: 'Débutant' },
    { value: 'intermediate', label: 'Intermédiaire' },
    { value: 'advanced', label: 'Avancé' },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'pending', label: 'En attente' },
    { value: 'published', label: 'Publié' },
    { value: 'archived', label: 'Archivé' }
  ];

  const moduleTypes = [
    { value: 'video', label: 'Vidéo', icon: Video, color: 'text-brand-600' },
    { value: 'pdf', label: 'PDF', icon: FileText, color: 'text-brand-600' },
    { value: 'text', label: 'Texte', icon: FileText, color: 'text-gray-600' },
    { value: 'link', label: 'Lien externe', icon: Link, color: 'text-brand-600' },
    { value: 'image', label: 'Image', icon: Image, color: 'text-brand-600' },
    { value: 'document', label: 'Document', icon: FileText, color: 'text-brand-600' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, courseRes] = await Promise.all([
          api.get<Category[]>('/categories'),
          api.get(`/courses/${courseId}`)
        ]);

        setCategories(categoriesRes.data);
        const courseData = courseRes.data.data;
        setCourse(courseData);

        // Set form values
        const formValues = {
          title: courseData.title || '',
          description: courseData.description || '',
          shortDescription: courseData.shortDescription || '',
          category: courseData.category?._id || '',
          price: courseData.price || 0,
          originalPrice: courseData.originalPrice,
          level: courseData.level || 'beginner',
          tags: courseData.tags?.join(',') || '',
          requirements: courseData.requirements?.join('\n') || '',
          objectives: courseData.objectives?.join('\n') || '',
          targetAudience: courseData.targetAudience?.join('\n') || '',
          settings: courseData.settings || {
            allowDownload: false,
            allowComments: false,
            sequentialAccess: false,
            timeLimit: 0
          },
          certificate: courseData.certificate || {
            enabled: false,
            passingScore: 70
          },
          estimatedDuration: courseData.estimatedDuration || 0,
          isPublished: courseData.isPublished || false,
          status: courseData.status || 'draft',
          sections: courseData.sections || []
        };

        Object.entries(formValues).forEach(([key, value]) => {
          setValue(key as keyof CourseForm, value);
        });

        if (courseData.thumbnail) {
          setThumbnailPreview(
            courseData.thumbnail.startsWith('http') 
              ? courseData.thumbnail
              : `${API_BASE_URL}${courseData.thumbnail}`
          );
        }
      } catch (error: any) {
        console.error('Error loading data:', error);
        toast.error(error.response?.data?.message || 'Erreur lors du chargement des données');
        navigate('/admin/courses');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, navigate, setValue]);

  const onSubmit = async (data: CourseForm) => {
    try {
      setSaving(true);
      const formData = new FormData();
      
      formData.append('id', courseId as string);

      // Basic fields
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('shortDescription', data.shortDescription);
      formData.append('category', data.category);
      formData.append('price', data.price.toString());
      if (data.originalPrice) formData.append('originalPrice', data.originalPrice.toString());
      formData.append('level', data.level);
      formData.append('estimatedDuration', data.estimatedDuration.toString());
      formData.append('isPublished', String(data.isPublished));
      formData.append('status', data.status);

      // Arrays
      formData.append('tags', data.tags.split(',').map(s => s.trim()).filter(Boolean).join(','));
      formData.append('requirements', data.requirements.split('\n').map(s => s.trim()).filter(Boolean).join('\n'));
      formData.append('objectives', data.objectives.split('\n').map(s => s.trim()).filter(Boolean).join('\n'));
      formData.append('targetAudience', data.targetAudience.split('\n').map(s => s.trim()).filter(Boolean).join('\n'));

      // Objects
      formData.append('settings', JSON.stringify(data.settings));
      formData.append('certificate', JSON.stringify(data.certificate));

      // Thumbnail
      if (thumbnail) {
        formData.append('thumbnail', thumbnail);
      }

      // Process sections and modules
      const processedSections = data.sections.map((section, sectionIndex) => {
        const processedModules = section.modules.map((module, moduleIndex) => {
          const moduleCopy = { ...module };
          
          // Handle file uploads
          if (module.file) {
            const fileKey = `module_file_${sectionIndex}_${moduleIndex}`;
            formData.append(fileKey, module.file);
            moduleCopy.fileKey = fileKey;
            delete moduleCopy.file;
          }
          
          // Clean up module data based on type
          if (moduleCopy.type === 'text') {
            delete moduleCopy.fileUrl;
            delete moduleCopy.externalUrl;
          } else if (moduleCopy.type === 'link') {
            moduleCopy.externalUrl = moduleCopy.content;
            delete moduleCopy.content;
            delete moduleCopy.fileUrl;
          } else {
            delete moduleCopy.content;
            delete moduleCopy.externalUrl;
          }

          return moduleCopy;
        });

        return { ...section, modules: processedModules };
      });

      formData.append('sections', JSON.stringify(processedSections));

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      };

      const response = await api.post('/courses', formData, config);

      if (response.status === 200 || response.status === 201) {
        const result = response.data;
        toast.success('Cours mis à jour avec succès');
        setCourse(result.data);
        
        // Update sections with new data from backend
        setValue('sections', result.data.sections || []);
        
        if (result.data.thumbnail) {
          setThumbnailPreview(
              course.thumbnail.startsWith('http') 
                ? course.thumbnail
                : `${API_BASE_URL}${course.thumbnail}`
            );
        }
      }
    } catch (error: any) {
      console.error('Save course error:', error);
      toast.error(error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  // Section management
  const addSection = (sectionData: { title: string; description: string }) => {
    const newSection: Section = {
      _id: uuidv4(), // Generate a temporary ID for new sections
      title: sectionData.title,
      description: sectionData.description,
      order: sections.length,
      modules: []
    };
    setValue('sections', [...sections, newSection]);
    setShowSectionModal(false);
    toast.success('Section ajoutée');
  };

  const updateSection = (sectionData: { title: string; description: string }) => {
    if (!editingSection) return;

    setValue('sections', sections.map(s => 
      s._id === editingSection._id 
        ? { ...s, ...sectionData }
        : s
    ));
    setShowSectionModal(false);
    setEditingSection(null);
    toast.success('Section mise à jour');
  };

  const deleteSection = (sectionId?: string) => {
    if (!confirm('Supprimer cette section et tous ses modules ?')) return;

    setValue('sections', sections.filter(s => s._id !== sectionId));
    toast.success('Section supprimée');
  };

  // Module management
  const addModule = (sectionId: string, moduleData: Module) => {
    setValue('sections', sections.map(section => 
      section._id === sectionId
        ? { 
            ...section, 
            modules: [...section.modules, { 
              ...moduleData, 
              _id: uuidv4(), // Generate a temporary ID for new modules
              order: section.modules.length 
            }] 
          }
        : section
    ));
    setShowModuleModal(false);
    toast.success('Module ajouté');
  };

  const updateModule = (sectionId: string, moduleData: Module) => {
    if (!editingModule.module) return;

    setValue('sections', sections.map(section => 
      section._id === sectionId
        ? {
            ...section,
            modules: section.modules.map(module =>
              module._id === editingModule.module?._id
                ? { ...module, ...moduleData }
                : module
            )
          }
        : section
    ));
    setShowModuleModal(false);
    setEditingModule({ sectionId: '', module: null });
    toast.success('Module mis à jour');
  };

  const deleteModule = (sectionId: string, moduleId?: string) => {
    if (!confirm('Supprimer ce module ?')) return;

    setValue('sections', sections.map(section => 
      section._id === sectionId
        ? {
            ...section,
            modules: section.modules.filter(module => module._id !== moduleId)
          }
        : section
    ));
    toast.success('Module supprimé');
  };

  const getModuleIcon = (type: string) => 
    moduleTypes.find(mt => mt.value === type)?.icon || FileText;
  
  const getModuleColor = (type: string) => 
    moduleTypes.find(mt => mt.value === type)?.color || 'text-gray-600';

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setThumbnail(acceptedFiles[0]);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
    maxSize: 5 * 1024 * 1024,
    multiple: false
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Modifier le cours
          </h1>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/courses')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={saving}
              className="flex items-center px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'info', name: 'Informations générales' },
              { id: 'content', name: 'Contenu du cours' },
              { id: 'settings', name: 'Paramètres' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {activeTab === 'info' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre du cours *
                  </label>
                  <input
                    {...register('title', { required: 'Le titre est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Ex: Expert gestion GPS et analyse des données"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-brand-600">{errors.title.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description complète *
                  </label>
                  <textarea
                    {...register('description', { required: 'La description est requise' })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Décrivez en détail le contenu et les objectifs de votre cours..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-brand-600">{errors.description.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description courte *
                  </label>
                  <input
                    {...register('shortDescription', {
                      required: 'La description courte est requise',
                      maxLength: {
                        value: 150,
                        message: 'La description courte ne doit pas dépasser 150 caractères'
                      }
                    })}
                    maxLength={150}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Résumé en une phrase (max 150 caractères)"
                  />
                  {errors.shortDescription && (
                    <p className="mt-1 text-sm text-brand-600">{errors.shortDescription.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie *
                  </label>
                  <select
                    {...register('category', { required: 'La catégorie est requise' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-brand-600">{errors.category.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau *
                  </label>
                  <select
                    {...register('level', { required: 'Le niveau est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">Sélectionner un niveau</option>
                    {levels.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                  {errors.level && (
                    <p className="mt-1 text-sm text-brand-600">{errors.level.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix (€) *
                  </label>
                  <input
                    {...register('price', { 
                      required: 'Le prix est requis', 
                      min: { value: 0, message: 'Le prix doit être positif' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="299.00"
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-brand-600">{errors.price.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix original (€)
                  </label>
                  <input
                    {...register('originalPrice', { 
                      min: { value: 0, message: 'Le prix doit être positif' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="399.00"
                  />
                  {errors.originalPrice && (
                    <p className="mt-1 text-sm text-brand-600">{errors.originalPrice.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durée estimée (heures)
                  </label>
                  <input
                    {...register('estimatedDuration', { 
                      min: { value: 0, message: 'La durée doit être positive' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="10"
                  />
                  {errors.estimatedDuration && (
                    <p className="mt-1 text-sm text-brand-600">{errors.estimatedDuration.message}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isPublished')}
                    className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">
                    Publier le cours
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut *
                  </label>
                  <select
                    {...register('status', { required: 'Le statut est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-brand-600">{errors.status.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image de couverture
                  </label>
                  <div 
                    {...getRootProps()} 
                    className={`border-2 ${isDragActive ? 'border-brand-400 bg-brand-50' : 'border-dashed border-gray-300'} rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors`}
                  >
                    <input {...getInputProps()} />
                    {thumbnailPreview ? (
                      <div className="flex flex-col items-center">
                        <img 
                          src={thumbnailPreview.startsWith('blob:') || thumbnailPreview.startsWith('data:') ? 
                            thumbnailPreview : 
                            thumbnailPreview
                          }
                          alt="Thumbnail preview" 
                          className="h-40 object-cover rounded-lg mb-4" 
                        />
                        <p className="text-sm text-gray-600">
                          Cliquez ou glissez-déposez pour changer l'image
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-12 w-12 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          Cliquez ou glissez-déposez une image ici
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG, GIF jusqu'à 5MB
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (séparés par des virgules)
                  </label>
                  <input
                    {...register('tags')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Ex: GPS, Analyse, Données"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prérequis (un par ligne)
                  </label>
                  <textarea
                    {...register('requirements')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Ex: Connaissance de base en informatique"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Objectifs d'apprentissage (un par ligne)
                  </label>
                  <textarea
                    {...register('objectives')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Ex: Maîtriser les concepts fondamentaux"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public cible (un par ligne)
                  </label>
                  <textarea
                    {...register('targetAudience')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Ex: Professionnels du secteur agricole"
                  />
                </div>
              </div>
            </form>
          )}

          {activeTab === 'content' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Sections du cours</h3>
                <button
                  onClick={() => {
                    setEditingSection(null);
                    setShowSectionModal(true);
                  }}
                  className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une section
                </button>
              </div>

              {sections.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune section créée</h3>
                  <p className="mb-4">Commencez par créer votre première section de cours</p>
                  <button
                    onClick={() => {
                      setEditingSection(null);
                      setShowSectionModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer la première section
                  </button>
                </div>
              )}

              <div className="space-y-4">
                {sections.map((section) => (
                  <div key={section._id || uuidv4()} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{section.title}</h4>
                        {section.description && (
                          <p className="text-gray-600 mt-1">{section.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingModule({ sectionId: section._id || '', module: null });
                            setShowModuleModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Ajouter un module"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => {
                            setEditingSection(section);
                            setShowSectionModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Modifier la section"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => deleteSection(section._id)}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Supprimer la section"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {section.modules.map((module) => {
                        const Icon = getModuleIcon(module.type);
                        return (
                          <div key={module._id || uuidv4()} className="flex items-center p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3 flex-1">
                              <Icon className={`h-5 w-5 ${getModuleColor(module.type)}`} />
                              <div>
                                <h5 className="font-medium text-gray-900">{module.title}</h5>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span>{moduleTypes.find(mt => mt.value === module.type)?.label}</span>
                                  {module.duration > 0 && <span>{module.duration} min</span>}
                                  {module.isRequired && <span className="text-brand-600">Requis</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => {
                                  setEditingModule({ sectionId: section._id || '', module });
                                  setShowModuleModal(true);
                                }}
                                className="p-1 text-gray-400 hover:text-brand-600 transition-colors"
                                title="Modifier le module"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => deleteModule(section._id || '', module._id)}
                                className="p-1 text-gray-400 hover:text-brand-600 transition-colors"
                                title="Supprimer le module"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {section.modules.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>Aucun module dans cette section</p>
                          <button
                            onClick={() => {
                              setEditingModule({ sectionId: section._id || '', module: null });
                              setShowModuleModal(true);
                            }}
                            className="mt-2 text-brand-600 hover:text-brand-700"
                          >
                            Ajouter le premier module
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Accès et permissions</h3>
                <p className="text-sm text-gray-500 mb-4">Contrôlez ce que les étudiants peuvent faire dans ce cours.</p>
                <div className="space-y-4">
                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <span>
                      <span className="block text-sm font-medium text-gray-900">Téléchargement des fichiers</span>
                      <span className="block text-sm text-gray-500">Autoriser les étudiants à télécharger les supports (PDF, images).</span>
                    </span>
                    <input
                      type="checkbox"
                      {...register('settings.allowDownload')}
                      className="h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 shrink-0 ml-4"
                    />
                  </label>
                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <span>
                      <span className="block text-sm font-medium text-gray-900">Commentaires</span>
                      <span className="block text-sm text-gray-500">Autoriser les étudiants à commenter les modules du cours.</span>
                    </span>
                    <input
                      type="checkbox"
                      {...register('settings.allowComments')}
                      className="h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 shrink-0 ml-4"
                    />
                  </label>
                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <span>
                      <span className="block text-sm font-medium text-gray-900">Accès séquentiel</span>
                      <span className="block text-sm text-gray-500">Les modules doivent être complétés dans l'ordre, un par un.</span>
                    </span>
                    <input
                      type="checkbox"
                      {...register('settings.sequentialAccess')}
                      className="h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 shrink-0 ml-4"
                    />
                  </label>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900">Limite de temps</h3>
                <p className="text-sm text-gray-500 mb-4">Durée maximale accordée à l'étudiant pour compléter le cours. Laissez 0 pour illimité.</p>
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Limite de temps (heures)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    {...register('settings.timeLimit', { valueAsNumber: true, min: { value: 0, message: 'La valeur doit être positive' } })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="0"
                  />
                  {errors.settings?.timeLimit && (
                    <p className="mt-1 text-sm text-brand-600">{errors.settings.timeLimit.message}</p>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900">Certificat</h3>
                <p className="text-sm text-gray-500 mb-4">Configurez la délivrance automatique d'un certificat de fin de cours.</p>
                <div className="space-y-4">
                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <span>
                      <span className="block text-sm font-medium text-gray-900">Activer le certificat</span>
                      <span className="block text-sm text-gray-500">Générer un certificat lorsque l'étudiant termine le cours.</span>
                    </span>
                    <input
                      type="checkbox"
                      {...register('certificate.enabled')}
                      className="h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 shrink-0 ml-4"
                    />
                  </label>
                  <div className="max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Score de réussite (%)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      {...register('certificate.passingScore', { valueAsNumber: true, min: { value: 0, message: 'Minimum 0' }, max: { value: 100, message: 'Maximum 100' } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      placeholder="70"
                    />
                    {errors.certificate?.passingScore && (
                      <p className="mt-1 text-sm text-brand-600">{errors.certificate.passingScore.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">Score minimum requis (quizzes et évaluations) pour obtenir le certificat.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section Modal */}
        {showSectionModal && (
          <SectionModal
            onClose={() => {
              setShowSectionModal(false);
              setEditingSection(null);
            }}
            onSave={editingSection ? updateSection : addSection}
            section={editingSection}
          />
        )}

        {/* Module Modal */}
        {showModuleModal && (
          <ModuleModal
            onClose={() => {
              setShowModuleModal(false);
              setEditingModule({ sectionId: '', module: null });
            }}
            onSave={(moduleData) => 
              editingModule.module 
                ? updateModule(editingModule.sectionId, moduleData) 
                : addModule(editingModule.sectionId, moduleData)
            }
            module={editingModule.module}
            moduleTypes={moduleTypes}
            uploadProgress={uploadProgress}
          />
        )}
      </div>
    </Layout>
  );
};

// Section Modal Component
const SectionModal: React.FC<{
  onClose: () => void;
  onSave: (data: { title: string; description: string }) => void;
  section?: Section | null;
}> = ({ onClose, onSave, section }) => {
  const [title, setTitle] = useState(section?.title || '');
  const [description, setDescription] = useState(section?.description || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim()) {
      onSave({ title: title.trim(), description: description.trim() });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {section ? 'Modifier la section' : 'Nouvelle section'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre de la section *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              placeholder="Ex: Introduction aux données GPS"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:focus:border-brand-500"
              placeholder="Décrivez le contenu de cette section..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              {section ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Module Modal Component
const ModuleModal: React.FC<{
  onClose: () => void;
  onSave: (data: Module) => void;
  module?: Module | null;
  moduleTypes: any[];
  uploadProgress?: number | null;
}> = ({ onClose, onSave, module, moduleTypes, uploadProgress }) => {
  const [formData, setFormData] = useState<Module>({
    title: module?.title || '',
    description: module?.description || '',
    type: module?.type || 'text',
    content: module?.type === 'text' ? module?.content : 
             module?.type === 'link' ? module?.externalUrl || module?.content : '',
    duration: module?.duration || 0,
    isRequired: module?.isRequired ?? false,
    fileUrl: module?.fileUrl,
    externalUrl: module?.externalUrl,
  });
  const [file, setFile] = useState<File | undefined>(undefined);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [clientUploadProgress, setClientUploadProgress] = useState<number | null>(null);

  useEffect(() => {
    if (module?.fileUrl && ['video', 'pdf', 'image', 'document'].includes(module.type)) {
      setFilePreview(`${API_BASE_URL}${module.fileUrl}`);
    } else {
      setFilePreview(null);
    }
  }, [module]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title.trim()) {
      const submitData: Module = { 
        ...formData,
        file: file,
      };
      onSave(submitData);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setClientUploadProgress(0);

      const reader = new FileReader();
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setClientUploadProgress(progress);
        }
      };
      reader.onloadend = () => {
        setClientUploadProgress(100);
        setTimeout(() => setClientUploadProgress(null), 1000); // Hide progress bar after a short delay
        if (selectedFile.type.startsWith('image/')) {
          setFilePreview(reader.result as string);
        } else {
          setFilePreview(selectedFile.name);
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const needsFile = ['video', 'pdf', 'image', 'document'].includes(formData.type);
  const needsContent = ['text', 'link'].includes(formData.type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {module ? 'Modifier le module' : 'Nouveau module'}
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du module *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Ex: Installation et configuration"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de module *
              </label>
              <select
                value={formData.type}
                onChange={(e) => {
                  const newType = e.target.value as any;
                  setFormData({ 
                    ...formData, 
                    type: newType,
                    content: '', // Reset content when type changes
                    file: undefined, // Reset file
                    fileUrl: newType === formData.type ? formData.fileUrl : undefined,
                    externalUrl: undefined
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                {moduleTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Décrivez le contenu de ce module..."
              />
            </div>

            {needsFile && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier {!module?.fileUrl && !file && '*'}
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  accept={
                    formData.type === 'video' ? 'video/*' :
                    formData.type === 'pdf' ? '.pdf' :
                    formData.type === 'image' ? 'image/*' :
                    '.doc,.docx,.pdf,.ppt,.pptx'
                  }
                  required={!module?.fileUrl && !file}
                />
                {clientUploadProgress !== null && (
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div 
                      className="bg-brand-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${clientUploadProgress}%` }}
                    ></div>
                  </div>
                )}
                {uploadProgress !== null && (
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div 
                      className="bg-brand-600 h-2.5 rounded-full" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
                {(filePreview || module?.fileUrl) && formData.type === 'image' && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-1">Aperçu :</p>
                    <img 
                      src={filePreview || `${API_BASE_URL}${module?.fileUrl}`} 
                      alt="Preview" 
                      className="h-40 object-contain rounded-lg border border-gray-200" 
                    />
                  </div>
                )}
                {(filePreview || module?.fileUrl) && formData.type !== 'image' && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      Fichier actuel : {filePreview ? filePreview.split('/').pop() : module?.fileUrl?.split('/').pop()}
                    </p>
                  </div>
                )}
              </div>
            )}

            {needsContent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.type === 'link' ? 'URL *' : 'Contenu *'}
                </label>
                {formData.type === 'text' ? (
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Saisissez le contenu textuel..."
                    required
                  />
                ) : (
                  <input
                    type="url"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="https://..."
                    required
                  />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="0"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Module requis</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
               Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              {module ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseEdit;